import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";
import { useRatehawkRegionSearch } from "./useRatehawkRegionSearch";
import type { SearchRatehawkRegionRow } from "@/integrations/supabase/rpc-types";

export interface CityAutocompletePlace {
  name: string;
  address: string;
  lat: number;
  lng: number;
  regionId: number;
}

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect: (place: CityAutocompletePlace) => void;
  placeholder?: string;
  className?: string;
}

const DEBOUNCE_MS = 300;

export function CityAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Search city...",
  className,
}: CityAutocompleteProps) {
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { results, isLoading } = useRatehawkRegionSearch(debouncedQuery);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setIsOpen(results.length > 0);
  }, [results]);

  const handleInputChange = (text: string) => {
    onChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text) {
      setDebouncedQuery("");
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => setDebouncedQuery(text), DEBOUNCE_MS);
  };

  const handleSelect = (region: SearchRatehawkRegionRow) => {
    const address = [region.name, region.country_name].filter(Boolean).join(", ");
    onChange(region.name);
    onPlaceSelect({
      name: region.name,
      address,
      lat: region.center_latitude!,
      lng: region.center_longitude!,
      regionId: region.id,
    });
    setDebouncedQuery("");
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
          <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
        </div>
      )}
      <Input
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => { if (results.length > 0) setIsOpen(true); }}
        placeholder={placeholder}
        className={`pl-10 ${className || ""}`}
      />

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-md border border-gray-200 shadow-lg max-h-60 overflow-y-auto">
          {results.map((region) => (
            <button
              key={region.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-start gap-2 transition-colors"
              onClick={() => handleSelect(region)}
            >
              <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{region.name}</p>
                {region.country_name && (
                  <p className="text-sm text-gray-500 truncate">{region.country_name}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
